import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteIconButton from './DeleteIconButton';
import { Divider, FormControlLabel, Grid, IconButton, Switch, TextField, Tooltip, Typography } from '@mui/material';
import grey from '@mui/material/colors/grey';
import { makeStyles } from '@mui/styles';
import React from 'react';
import { useDropzone } from 'react-dropzone';

import QuillEditor from '../components/QuillEditor';
import { getDayNumber } from '../explore/utils';
import { asJSONPath } from '../utils';
import { useIsWidthUp } from '../utils';
import { makeRandomHash } from '../utils/windowns';
import UploadImageButton from './UploadImageButton';

const useStyles = makeStyles((theme) => ({
    root: {
        maxHeight: `calc(${theme.cp.contentHeight} - 3rem)`,
    },
    ActivityWrapper: {
        padding: '0',
        paddingLeft: '3rem !important',
        marginLeft: '0.25rem',
        [theme.breakpoints.down('md')]: {
            paddingLeft: '1rem !important',
            marginLeft: '0.25rem',
        },
    },
    dividerGridItem: {
        paddingTop: theme.spacing(1),
    },
    arrowButton: {
        padding: '0.5rem',
        border: `solid 1px ${grey[400]}`,
        borderRadius: '5px',
        cursor: 'pointer',
        background: 'transparent',
        height: '50px',
        color: theme.palette.primary.main,
        [theme.breakpoints.down('sm')]: {
            padding: '0.25rem',
        },
    },

}));

const compare = (a, b) => {
    if (a.sequence > b.sequence) {
        return 1;
    }
    if (a.sequence < b.sequence) {
        return -1;
    }
    return 0;
};

const TripflowDay = (props) => {
    const classes = useStyles();

    const dayStart = +props.number + 1;
    const dayEnd = dayStart + +props.day.day_span - 1;
    const multipleDays = +props.day.day_span > 1;
    const isExisting = props.day.image && props.day.image.p && props.day.image.s;
    const isUpload = !isExisting;
    const imageProp = isUpload ? { upload: props.day.image } : { image: props.day.image };

    const onDrop = React.useCallback(
        (acceptedFiles) => {
            // console.log('onDrop', acceptedFiles, props.day);
            props.onImageSelect && props.onImageSelect(asJSONPath(`days[${props.idx}]`, { image: acceptedFiles[0], upload: true }), props.idx);
            // props.onUpload && props.onUpload(getUpdate(), props.idx, acceptedFiles[0]);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        [props.day]
    );



    const dz = useDropzone({
        noClick: true,
        noKeyboard: true,
        maxFiles: 1,
        onDrop: onDrop,
        multiple: false,
    });
    const onChange = (ev) => {
        props.onChange && props.onChange(asJSONPath(`days[${props.idx}]`, { [ev.target.name]: ev.target.value }), props.idx);
    };
    const onDelete = (ev) => {
        props.onDelete && props.onDelete(props.idx, ev);
    };
    const onBlur = (ev) => {
        if (!ev.currentTarget.contains(ev.relatedTarget) && props.day._changed) {
            props.onBlur && props.onBlur(props.idx, props.day, ev);
        }
    };

    const handleEditorChange = (content) => {
        onChange({
            target: {
                name: 'description',
                value: content,
            },
        });
    };

    const swapItems = (i, j) => {
        if (i < j && props.itinerary?.days?.length > j) {
            props.onChange && props.onChange(asJSONPath(`days[${i}]`, { sequence: props.day.sequence + 1 }), i);
            props.onChange && props.onChange(asJSONPath(`days[${i + 1}]`, { sequence: props.day.sequence - 1 }), i + 1);
        }
        if (i > j && i > 0) {
            props.onChange && props.onChange(asJSONPath(`days[${i}]`, { sequence: props.day.sequence - 1 }), i);
            props.onChange && props.onChange(asJSONPath(`days[${i - 1}]`, { sequence: props.day.sequence + 1 }), i + 1);
        }
    };

    const onRemoveImage = () => {
        onChange({
            target: {
                name: 'image',
                value: null,
            },
        });
    }
    return (
        <div className={classes.dayRoot} onBlur={onBlur}>
            <Grid container direction="row" spacing={2}>
                {!props.itineraryCustomDayTitles && (

                    <Grid item xs={4} sm={2}>
                        <TextField
                            disabled={!props.hasEditPermission}
                            variant="filled"
                            label="Day Span"
                            name="day_span"
                            size="small"
                            type="number"
                            value={props.day.day_span}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                                inputProps: { min: 1, step: 1 },
                            }}
                            onChange={onChange}
                        />
                    </Grid>

                )}
                {!props.itineraryCustomDayTitles && 
                <Grid item xs={6} sm={3} container alignItems="center">
                     <Typography variant="body2">{multipleDays ? `Days ${dayStart} - ${dayEnd}` : `Day ${dayStart}`}</Typography>
                </Grid>}
                {!props.itineraryCustomDayTitles && 
                <Grid item xs={2} sm={7} container justifyContent="flex-end">
                    <DeleteIconButton title="Delete day" size="medium" fontSize="medium" tabIndex="-1" onClick={onDelete} allowed={props.hasEditPermission} />
                </Grid>}

                <Grid item xs={12} sm={12} md={5} lg={5} style={{position:'relative'}}>
                    <input {...dz.getInputProps()} />
                    {/* CP-5545: positioned against the image itself, inset from its corner like Edit Attraction's,
                        not against the grid cell (which put it flush with the image's right edge). */}
                    <div style={{ position: 'relative' }}>
                        <UploadImageButton disabled={!props.hasEditPermission} key={JSON.stringify(imageProp)} imageAction="overlay" borderRadius={10} width={'100%'} height={props.isSmallScreen ? 240 : 400} onClick={dz.open} isUpload={isUpload} image={props.day.image} />
                        {props.day.image && (
                            <span style={{ position: 'absolute', top: 11, right: 11, zIndex: 1000 }}>
                                <DeleteIconButton title="Delete image" overImage onClick={onRemoveImage} allowed={props.hasEditPermission} />
                            </span>
                        )}
                    </div>
                </Grid>   
                <Grid item xs={12} sm={12} md={7} lg={7} container direction="row" alignItems="center" spacing={2} className={classes.ActivityWrapper}>
                    {props.hasEditPermission && (
                        <Grid item xs={3} sm={'auto'}  container justifyContent="flex-start">
                            <div style={{ display: 'flex', gap: '0.25rem' }}>
                                <Tooltip title="Move up">
                                    <button className={classes.arrowButton} style={{ color: props.idx === 0 && grey[400], cursor: props.idx === 0 && 'default' }} onClick={() => swapItems(props.idx, props.idx - 1)}>
                                        <ArrowUpwardIcon />
                                    </button>
                                </Tooltip>
                                <Tooltip title="Move down">
                                    <button className={classes.arrowButton} style={{ color: props.idx === (props.itinerary?.days?.length || 0) - 1 && grey[400], cursor: props.idx === (props.itinerary?.days?.length || 0) - 1 && 'default' }} onClick={() => swapItems(props.idx, props.idx + 1)}>
                                        <ArrowDownwardIcon />
                                    </button>
                                </Tooltip>
                            </div>
                        </Grid>
                    )}

                    <Grid item xs={8} sm={7.5} lg={8.5} xl={9} pr={1}>
                        <TextField disabled={!props.hasEditPermission} variant="filled" size="small" label="Span Title" name="title" value={props.day.title} onChange={onChange} fullWidth InputLabelProps={{ shrink: true }} />
                    </Grid>
                    {props.itineraryCustomDayTitles && <Grid item xs={1} container justifyContent="flex-end" alignContent="center" >
                        <DeleteIconButton title="Delete day" size="medium" fontSize="medium" tabIndex="-1" onClick={onDelete} allowed={props.hasEditPermission} />
                    </Grid>}

                    <Grid item xs={12} container>
                        {!props.hasEditPermission ?
                            <TextField minHeight={'300px'} minRows={10} disabled={true} fullWidth variant="filled" size="small" label="Span Description" multiline value={props.day.description} />
                            :
                            <QuillEditor
                                value={props.day.description}
                                onChange={handleEditorChange}
                                style={{ width: '100%', minHeight: '300px' }}
                            />}


                        {/* <TextField
                            variant="filled"
                            size="small"
                            label="Span Description"
                            name="description"
                            value={props.day.description}
                            onChange={onChange}
                            fullWidth
                            multiline
                            rows={7}
                            rowsMax={7}
                            InputLabelProps={{ shrink: true, }}
                            /> */}

                    </Grid>
                </Grid>
            </Grid>
        </div>
    );
};

const DayDivider = (props) => {
    const onClick = (ev) => {
        props.onDayAdd && props.onDayAdd(props.idx, ev);
    };
    return (
        <Grid container justifyContent="space-between" spacing={2} pb={2} alignItems="center">
            <Grid item container justifyContent="flex-start" xs={1} >
                <Tooltip title="Add day">
                    <span>
                        <IconButton disabled={!props.hasEditPermission} onClick={onClick}>
                            <AddIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            </Grid>
            <Grid item xs={11}>
                <Divider fullWidth />
            </Grid>
        </Grid>
    );
};

const emptyDay = {
    id: null,
    day_span: 1,
    title: '',
    subtitle: '',
    description: '',
    footer: '',
    image: null,
    sequence: null,
    timeslots: [],
    _changed: true,
};

const calculateDayStart = (days) => {
    const data = {};
    let start = 0;
    days.forEach((day, idx) => {
        data[idx] = start;
        start += +day.day_span;
    });
    return data;
};

const TripflowEditor = (props) => {
    const classes = useStyles();
    const days = props.days || [];
    const [upload, setUpload] = React.useState(false);
    const isSmallScreen = !useIsWidthUp('sm');

    // const smallScreenOn = props.smallScreenOn || 'sm';
    // const isSmallScreen = !isWidthUp(smallScreenOn, props.width);
    // const isSmallScreen = !isWidthUp(smallScreenOn, props.width);

    const onDayAdd = (idx, ev) => {
        props.onInsert && props.onInsert({ ...emptyDay, _inthash: makeRandomHash(8) }, idx + 1);
    };

    const onDayChange = (update, idx) => {
        onChange(update, upload);
    };

    const onDayImageSelect = (update, idx) => {
        setUpload(true);
        onChange(update, true);
    };
    const onDayDelete = (idx) => {
        props.onDelete && props.onDelete(idx);
    };

    const onDayBlur = (idx, day, ev) => {
        onChange(days, upload);
    };

    const dayStart = calculateDayStart(days);

    const onChange = (update, upload) => {
        props.onChange && props.onChange(update, upload);
    };

    const onSwitchChange = (ev) => {
        props.setItinerary((prevState) => ({ ...prevState, [ev.target.name]: ev.target.checked }));
    };

    return (
        <div className={classes.root}>
            <Grid container direction="row" spacing={2}>
                <Grid item xs={12}>
                    <FormControlLabel control={<Switch checked={props.itinerary?.custom_day_titles} name="custom_day_titles" color="primary" onChange={onSwitchChange} />} label="Custom Day Titles" />
                </Grid>
                {!props.itinerary?.custom_day_titles && (
                    <Grid item xs={12}>
                        <TextField label="Number of Days" type="number" variant="filled" size="small" name="day_count" value={getDayNumber(props.itinerary)} disabled InputLabelProps={{ shrink: true }} helperText="Add/remove days on day-by-day highlights tab to change the number of days." />
                    </Grid>
                )}
                {days.sort(compare).map((day, idx) => (
                    <React.Fragment key={`${idx}-${day._inthash}-${day.id}`}>
                        <Grid item xs={12}>
                            <TripflowDay
                                itinerary={props.itinerary}
                                day={day}
                                idx={idx}
                                number={dayStart[idx]}
                                onChange={onDayChange}
                                onBlur={onDayBlur}
                                onDelete={onDayDelete}
                                onImageSelect={onDayImageSelect}
                                itineraryCustomDayTitles={props.itinerary?.custom_day_titles}
                                isSmallScreen={isSmallScreen}
                                hasEditPermission={props.hasEditPermission}
                            />
                        </Grid>
                        {idx < days.length - 1 ? (
                            <Grid item xs={12} container alignItems="center">
                                <DayDivider hasEditPermission={props.hasEditPermission} onDayAdd={onDayAdd} idx={idx} />
                            </Grid>
                        ) : null}
                    </React.Fragment>
                ))}
                <Grid item xs={12} container alignItems="center">
                    <DayDivider hasEditPermission={props.hasEditPermission} onDayAdd={onDayAdd} idx={days.length - 1} />
                </Grid>
            </Grid>
        </div>
    );
};

export default TripflowEditor;
